import {
  AreaMeasurementMetadata,
  DepthMeasurementMetadata,
  DistanceMeasurementMetadata,
  MeasuredPointMetadata,
  Measurement,
  MeasurementFactory,
  MeasurementMetadata,
  MeasurementType,
  PointToLineMeasurementMetadata,
  PolylineMeasurementMetadata,
  ReferenceDistancePointMetadata,
  TipDefectMeasurementMetadata
} from "@evident/ngx-media-viewer";
import { VISOL_MEASUREMENT_TYPES } from "../inspection-data-management/image-edit-dialog/measurement-merry/measurement.type";

/** The media viewer measurement metadata version ViSOL writes (`Measurement.toMetadata()`) and knows how to read. */
export const MERRY_MEASUREMENT_METADATA_VERSION = 1;

interface Vector2Metadata {
  x: number;
  y: number;
}

interface Vector3Metadata {
  x: number;
  y: number;
  z: number;
}

/** A reference point as media-viewer 0.6.x serialized it (no reliability). */
interface LegacyMeasuredPoint {
  points2D: Vector2Metadata;
  points3D: Vector3Metadata;
  stereo2D?: Vector2Metadata;
}

/**
 * A measurement as ViSOL stored it with media-viewer 0.6.x: no `version`, the values in `parameters`, and Depth /
 * PointToLine as a single measured point (points2D = [measured point, foot point]) whose reference plane / line is
 * `parameters.referenceLines[0]`.
 */
interface ViSOLLegacyMeasurement {
  type: MeasurementType;
  comment?: string;
  measureId?: string;
  isVisible?: boolean;
  points2D?: Vector2Metadata[];
  points3D?: Vector3Metadata[];
  stereo2D?: Vector2Metadata[];
  parameters?: {
    length?: number;
    area?: number;
    perimeter?: number;
    referenceLines?: LegacyMeasuredPoint[][];
  };
}

/** A stored entry whose type is one ViSOL measures, whatever its version. */
type StoredMeasurement = { type: MeasurementType; version?: unknown; parameters?: unknown };

/** Media-viewer 0.6.x stored no reliability; Reliability.HIGH is the media viewer default for a measured point. */
const LEGACY_RELIABILITY = 0;

/**
 * Reads the decrypted XMP "Measurements" list into media viewer measurements. Merry and ViSOL now both store
 * `Measurement.toMetadata()` entries (`version: 1`); the measurements ViSOL saved with media-viewer 0.6.x are
 * converted to that format first. Only the types ViSOL measures (VISOL_MEASUREMENT_TYPES) are read: Merry can
 * store others (TipClearance, CircularArc...) that ViSOL neither draws nor lists, so they are skipped.
 */
export class MerryMeasurementMetadata {
  static toMeasurements(stored: unknown, viewerId: string, label: string): Measurement[] {
    if (!Array.isArray(stored)) {
      console.warn(`${label}: the Measurements tag is not a list, ignored`, stored);
      return [];
    }

    return stored.flatMap((entry, index) => {
      const entryLabel = `${label} #${index}`;
      try {
        const measurement = MerryMeasurementMetadata.toMeasurement(entry, viewerId, entryLabel);
        return measurement ? [measurement] : [];
      } catch (error) {
        console.error(`${entryLabel}: could not be read, skipped`, entry, error);
        return [];
      }
    });
  }

  private static toMeasurement(entry: unknown, viewerId: string, label: string): Measurement | null {
    if (!entry || typeof entry !== "object" || !("type" in entry)) {
      console.warn(`${label}: not a measurement, skipped`, entry);
      return null;
    }
    if (!MerryMeasurementMetadata.isViSOLType(entry.type)) {
      console.warn(`${label}: type "${String(entry.type)}" is not one ViSOL measures, skipped`, entry);
      return null;
    }

    const stored = entry as StoredMeasurement;
    if (MerryMeasurementMetadata.isViSOLLegacy(stored)) {
      const metadata = MerryMeasurementMetadata.fromViSOLLegacy(stored, label);
      return metadata ? MeasurementFactory.fromMetadata(metadata, viewerId) : null;
    }
    return MeasurementFactory.fromMetadata(MerryMeasurementMetadata.asKnownVersion(stored, label), viewerId);
  }

  private static isViSOLType(type: unknown): type is MeasurementType {
    return VISOL_MEASUREMENT_TYPES.includes(type as MeasurementType);
  }

  /** ViSOL with media-viewer 0.6.x stored no `version` and the values under `parameters`. */
  private static isViSOLLegacy(stored: StoredMeasurement): stored is ViSOLLegacyMeasurement {
    return stored.version === undefined && stored.parameters !== undefined;
  }

  /**
   * Merry and ViSOL both store `version: 1`. An entry without a version that is not the 0.6.x shape, or one with a
   * version this build does not know, is read as version 1 on a best-effort basis (and logged) rather than dropped: a
   * measurement the media viewer can still read is better than a missing one in a report, and the caller skips the
   * entry when the media viewer cannot read it.
   */
  private static asKnownVersion(stored: StoredMeasurement, label: string): MeasurementMetadata {
    if (stored.version === undefined) {
      console.warn(`${label}: no measurement version, read as version ${MERRY_MEASUREMENT_METADATA_VERSION}`, stored);
    } else if (stored.version !== MERRY_MEASUREMENT_METADATA_VERSION) {
      console.warn(
        `${label}: measurement version "${String(stored.version)}" is unknown, read as version ${MERRY_MEASUREMENT_METADATA_VERSION}`,
        stored
      );
    }
    return { ...stored, version: MERRY_MEASUREMENT_METADATA_VERSION } as MeasurementMetadata;
  }

  private static fromViSOLLegacy(legacy: ViSOLLegacyMeasurement, label: string): MeasurementMetadata | null {
    const base = {
      version: 1 as const,
      comment: legacy.comment ?? "",
      measureId: legacy.measureId ?? MeasurementFactory.generateId(),
      isVisible: legacy.isVisible ?? true
    };
    const points = {
      points2D: legacy.points2D ?? [],
      stereo2D: legacy.stereo2D ?? [],
      points3D: legacy.points3D ?? []
    };
    const parameters = legacy.parameters ?? {};
    const referenceLines = (parameters.referenceLines ?? []).map((line) => line.map(toMeasuredPointMetadata));

    switch (legacy.type) {
      case MeasurementType.DISTANCE: {
        const metadata: DistanceMeasurementMetadata = { ...base, ...points, type: legacy.type, length: parameters.length ?? 0 };
        return metadata;
      }
      case MeasurementType.POLYLINE: {
        const metadata: PolylineMeasurementMetadata = { ...base, ...points, type: legacy.type, length: parameters.length ?? 0 };
        return metadata;
      }
      case MeasurementType.AREA: {
        const metadata: AreaMeasurementMetadata = {
          ...base,
          ...points,
          type: legacy.type,
          area: parameters.area ?? 0,
          perimeter: parameters.perimeter ?? 0
        };
        return metadata;
      }
      case MeasurementType.TIP_DEFECT: {
        const metadata: TipDefectMeasurementMetadata = {
          ...base,
          ...points,
          type: legacy.type,
          area: parameters.area ?? 0,
          referenceLines: [referenceLines[0] ?? [], referenceLines[1] ?? []]
        };
        return metadata;
      }
      case MeasurementType.DEPTH: {
        // ViSOL stored the reference plane as a closed outline p1, p2, p3, p1.
        const metadata: DepthMeasurementMetadata = {
          ...base,
          type: legacy.type,
          referenceArea: withoutClosingPoint(referenceLines[0] ?? []),
          depthPoints: toDistancePoints(legacy, base.measureId, parameters.length)
        };
        return metadata;
      }
      case MeasurementType.POINT_TO_LINE: {
        const referenceLine = referenceLines[0] ?? [];
        const metadata: PointToLineMeasurementMetadata = {
          ...base,
          type: legacy.type,
          referenceLine,
          originalReferenceLine: referenceLine,
          distancePoints: toDistancePoints(legacy, base.measureId, parameters.length)
        };
        return metadata;
      }
      default:
        console.warn(`${label}: type "${String(legacy.type)}" is not one ViSOL measures, skipped`, legacy);
        return null;
    }
  }
}

const toMeasuredPointMetadata = (point: LegacyMeasuredPoint): MeasuredPointMetadata => ({
  points2D: point.points2D,
  points3D: point.points3D,
  stereo2D: point.stereo2D,
  reliability: LEGACY_RELIABILITY
});

/** ViSOL stored one measured point per Depth / PointToLine measurement: points2D = [measured point, foot point]. */
const toDistancePoints = (legacy: ViSOLLegacyMeasurement, measureId: string, length = 0): ReferenceDistancePointMetadata[] => {
  const pointAt = (index: number): MeasuredPointMetadata | undefined => {
    const point2D = legacy.points2D?.[index];
    const point3D = legacy.points3D?.[index];
    return point2D && point3D
      ? { points2D: point2D, points3D: point3D, stereo2D: legacy.stereo2D?.[index], reliability: LEGACY_RELIABILITY }
      : undefined;
  };
  const measuredPoint = pointAt(0);
  const footPoint = pointAt(1);

  return measuredPoint && footPoint ? [{ measuredPoint, footPoint, length, isVisible: true, id: `${measureId}-0` }] : [];
};

const withoutClosingPoint = (outline: MeasuredPointMetadata[]): MeasuredPointMetadata[] => {
  const first = outline[0]?.points2D;
  const last = outline[outline.length - 1]?.points2D;
  const isClosed = outline.length > 3 && !!first && !!last && first.x === last.x && first.y === last.y;

  return isClosed ? outline.slice(0, -1) : outline;
};
